import JoinCTA from '@/components/shared/JoinCTA';
import { Box } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { AnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Section, SectionParagraph, SectionTitle } from '../Content';
import { DEFAULT_SPACING, type DefaultSectionProps } from '../DefaultSectionProps';
import { Card } from './components';
import type { BundleProduct } from './defaults';
import { BUNDLE_PRODUCTS } from './defaults';

/* eslint-disable @typescript-eslint/naming-convention */
export const PRODUCT_MAP = {
  car_pet: '?p=car_and_pet',
  car_pet_renters: '?p=car_renters_and_pet',
  home_car: '?p=home_and_car',
  home_car_pet: '?p=home_car_and_pet',
  home_pet: '?p=home_and_pet',
  renters: '?p=home_us_renters',
  renters_car: '?p=car_and_renters',
  renters_car_home_pet: '?mps=1',
  renters_pet: '?p=renters_and_pet',
};
/* eslint-enable @typescript-eslint/naming-convention */

const hasSameLength = (a: string[], b: string[]): boolean => a.length === b.length;
const includesTheValues = (a: string[], b: string[]): boolean => a.every(i => b.includes(i));
const listAreEquals = (a: string[], b: string[]): boolean => hasSameLength(a, b) && includesTheValues(a, b);

const humanizeProduct = (key: string): string =>
  key.replaceAll('home_us_owners', 'home').replaceAll('home_us_renters', 'renters');

const keyToArray = (key: string): string[] => humanizeProduct(key).split('_');

const humanizeState = (state: string[]): string[] => state.map(i => humanizeProduct(i)).filter(Boolean);

const Title = styled(SectionTitle)`
  text-transform: capitalize;
`;

interface CtaProps {
  readonly isEnabled: boolean;
}

const Cta = styled(JoinCTA)<CtaProps>`
  display: block;
  transition: all ease 250ms;
  transform: ${({ isEnabled }: CtaProps) => (isEnabled ? 'translateY(-10px)' : 'translateY(0)')};
  pointer-events: ${({ isEnabled }: CtaProps) => (isEnabled ? 'auto' : 'none')};
  cursor: ${({ isEnabled }: CtaProps) => (isEnabled ? 'pointer' : 'not-allowed')};

  &&& {
    padding: 18px 64px;
    font-size: 1.8rem;
  }
`;

const CardContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
  max-width: 1140px;
  justify-content: center;
  align-items: stretch;
  gap: 20px;
  ${MEDIA_QUERIES.tablet`
         gap: 20px;
         grid-template-columns: repeat(4, minmax(0, 1fr));
    `}
  ${MEDIA_QUERIES.desktop`
         gap: 30px;
         grid-template-columns: repeat(4, minmax(0, 1fr));
    `}
`;

interface CallToActionProps {
  readonly isEnabled: boolean;
  readonly createUrlParam?: string | false;
  readonly onClick: () => void;
  readonly cta?: string;
}

const CallToAction: React.FC<CallToActionProps> = React.memo(({ isEnabled, createUrlParam, onClick, cta }) => {
  return (
    <Box mt={['50px', '70px']}>
      <Cta
        aria-disabled={!isEnabled}
        customUrl={`/onboarding/start${createUrlParam}`}
        disabled={!isEnabled}
        flavor="primary"
        glowing
        isEnabled={isEnabled}
        onClick={onClick}
      >
        {cta ?? 'Get a quote'}
      </Cta>
    </Box>
  );
});

CallToAction.displayName = 'CallToAction';
interface SectionIntroProps {
  readonly title?: string;
  readonly subtitle?: string;
}

const SectionIntro: React.FC<SectionIntroProps> = ({
  title = 'Complete Protection, Bundled By You',
  subtitle = 'Pick the insurance you need, and sign up in just a few minutes',
}) => {
  return (
    <>
      <Title>{parse(title)}</Title>
      <Box mb={['40px', '50px']} mt={'10px'}>
        <SectionParagraph>{parse(subtitle)}</SectionParagraph>
      </Box>
    </>
  );
};

export interface CardsComponentProps {
  readonly products?: string;
  readonly cta?: string;
}

export const CardsComponent: React.FC<CardsComponentProps> = ({ products, cta }) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const defaultProducts = useMemo(
    () => (products?.split(',') as BundleProduct['product'][] | undefined) ?? [],
    [products],
  );
  const [state, setState] = useState<BundleProduct['product'][]>(defaultProducts);

  const handleClick = (product: BundleProduct['product']): void => {
    const current = [...state];
    // remove item
    if (current.includes(product)) {
      setState(current.filter(i => i !== product));
      return;
    }

    // add item
    setState(currentState => [...currentState, product]);
  };

  const getIdDisabled = useCallback(
    (prod: BundleProduct['product']): boolean =>
      (prod === 'home_us_renters' && state.includes('home_us_owners')) ||
      (prod === 'home_us_owners' && state.includes('home_us_renters')),
    [state],
  );

  const createUrlParam = useMemo(() => {
    return state.length > 1
      ? Object.entries(PRODUCT_MAP)
          .map(([key, value]) => {
            if (listAreEquals(keyToArray(key), humanizeState(state))) return value;

            return false;
          })
          .find(i => i)
      : '';
  }, [state]);

  const isEnabled = useMemo(
    () => (defaultProducts.length >= 1 && state.length >= 1) || state.length > 1,
    [state.length, defaultProducts.length],
  );

  return (
    <>
      <CardContainer>
        {BUNDLE_PRODUCTS.map((item, idx) => (
          <Card
            idx={idx}
            isActive={state.includes(item.product)}
            isDisabled={getIdDisabled(item.product)}
            item={item}
            key={item.source}
            onClick={() => handleClick(item.product)}
          />
        ))}
      </CardContainer>

      <CallToAction
        createUrlParam={createUrlParam}
        cta={cta}
        isEnabled={isEnabled}
        onClick={() =>
          trackEvent('select_bundle_question_answered', {
            type: state.toString().replaceAll(',', '_'),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            mpp_flow: (state.length > 0).toString(),
          })
        }
      />
    </>
  );
};

export interface BuildByYouProps extends DefaultSectionProps {
  readonly title?: string;
  readonly subtitle?: string;
  readonly products?: string;
  readonly cta?: string;
}

export const BuildByYou: React.FC<BuildByYouProps> = ({ title, subtitle, products, cta, ...sectionProps }) => {
  return (
    <Section {...sectionProps} {...DEFAULT_SPACING}>
      <SectionIntro subtitle={subtitle} title={title} />
      <CardsComponent cta={cta} products={products} />
    </Section>
  );
};
